import React, { useEffect, useRef, useState } from 'react';
import { Button } from '../../../components/ui/button';
import { 
    AlertDialog, 
    AlertDialogCancel, 
    AlertDialogContent, 
    AlertDialogDescription, 
    AlertDialogFooter, 
    AlertDialogHeader, 
    AlertDialogTitle 
} from '../../../components/ui/alert-dialog';
// import { Label } from '../../../components/ui/label';
import { useDispatch, useSelector } from 'react-redux';
import FormField from '../../../components/FormField';
import { 
    loadExtPageData, 
    updateExtraction,
    getExtProjectsForMaster,
    getExtMetaData
} from '../../../store/extractions/action';
import constants from '../../../commonUtils/constants';
import { filterPincodeInput, isAllowedPincodeKey } from '../../../commonUtils/string.helper';

const UpdateProjectDetails = ({ isOpen, onClose, extractionData }) => {
    const dispatch = useDispatch();

    const { pageLoadData, projectsLoading, metaDataLoading, extractionData: reduxExtractionData } = useSelector((state) => state.Extractions);
    const [formData, setFormData] = useState({ 
        masterProject: '', 
        project: '',
        approxStartingPrice: '0',
        pincode: ''
    });
    const [errs, setErrs] = useState({ 
        masterProject: '', 
        project: '',
        approxStartingPrice: '',
        pincode: ''
    });
    // Track whether we triggered a metadata fetch so we only sync when we caused it
    const metaDataFetchedRef = useRef(false);

    // Load master projects when modal opens
    useEffect(() => {
        if (isOpen) {
            dispatch(loadExtPageData());
        }
    }, [isOpen, dispatch]);

    // Reset form (pre-fill existing values) when modal opens/closes
    useEffect(() => {
        if (isOpen) {
            metaDataFetchedRef.current = false;
            setFormData({ 
                masterProject: '', 
                project: '',
                approxStartingPrice: extractionData?.approxStartingPrice != null ? String(extractionData.approxStartingPrice) : '0',
                pincode: extractionData?.pincode || ''
            });
            setErrs({ 
                masterProject: '', 
                project: '',
                approxStartingPrice: '',
                pincode: ''
            });
        }
    }, [isOpen, extractionData]);

    // Sync metadata from Redux into local formData when metadata finishes loading
    useEffect(() => {
        if (!metaDataLoading && metaDataFetchedRef.current) {
            metaDataFetchedRef.current = false;
            setFormData(prev => ({
                ...prev,
                // Always update approxStartingPrice from metadata
                approxStartingPrice: reduxExtractionData?.approxStartingPrice != null
                    ? String(reduxExtractionData.approxStartingPrice)
                    : '0',
                // Only update pincode if metadata returned a non-null value; otherwise keep old pincode
                pincode: reduxExtractionData?.pincode != null && reduxExtractionData?.pincode !== ''
                    ? reduxExtractionData.pincode
                    : prev.pincode
            }));
        }
    }, [metaDataLoading, reduxExtractionData]);

    const handleInputChange = (field, value) => {
        // Special handling for pincode: filter to digits, max 6 chars
        if (field === 'pincode') {
            value = filterPincodeInput(value);
        }
        setFormData(prev => ({ ...prev, [field]: value }));
        setErrs(prev => ({ ...prev, [field]: '' }));

        // Load projects when master project is selected, reset project selection
        if (field === 'masterProject' && value && value !== '') {
            dispatch(getExtProjectsForMaster(value));
            setFormData(prev => ({ ...prev, masterProject: value, project: '' }));
        }

        // Fetch metadata when project is selected and masterProject is available
        if (field === 'project' && value && value !== '') {
            setFormData(prev => {
                const currentMasterProject = prev.masterProject;
                if (currentMasterProject && currentMasterProject !== '') {
                    metaDataFetchedRef.current = true;
                    dispatch(getExtMetaData({ masterProject: currentMasterProject, projectName: value }));
                }
                return { ...prev, project: value };
            });
        }
    };

    const saveProjectDetails = async (event) => {
        event.preventDefault();

        const newErrs = {};
        if (!formData.masterProject) newErrs.masterProject = 'Please select a Master Project.';
        if (!formData.project) newErrs.project = 'Please select a Project.';
        if (formData.approxStartingPrice === null || formData.approxStartingPrice === undefined || String(formData.approxStartingPrice).trim() === '')
            newErrs.approxStartingPrice = 'Please enter an Approximate Starting Price.';
        const pincode = String(formData.pincode || '').trim();
        if (!pincode) {
            newErrs.pincode = 'Please enter a Pincode.';
        } else if (!/^\d{6}$/.test(pincode)) {
            newErrs.pincode = 'Pincode must be exactly 6 digits.';
        }
        setErrs(newErrs);

        if (Object.keys(newErrs).length === 0) {
            dispatch(updateExtraction({
                id: extractionData.id,
                formData: { 
                    masterProject: formData.masterProject, 
                    project: formData.project,
                    approxStartingPrice: String(formData.approxStartingPrice),
                    pincode: formData.pincode
                },
                fieldName: 'project details'
            }));
            onClose();
        }
    };

    return (
        <AlertDialog open={isOpen} onOpenChange={onClose}>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>Update Project Details</AlertDialogTitle>
                    <AlertDialogDescription>
                        Update the Master Project and Project for extraction "{extractionData?.name}"
                    </AlertDialogDescription>
                </AlertDialogHeader>
                
                <div className='flex flex-col space-y-4 w-full mb-4'>
                    {/* Current Temporary Values Display */}
                    {extractionData?.tempMasterProjectName && extractionData?.tempProjectName && (
                        <div className="bg-slate-50 border border-slate-200 rounded-md p-4 space-y-2">
                            <p className="text-sm font-medium text-slate-700">Current Temporary Values:</p>
                            <div className="text-sm text-slate-600">
                                <p><span className="font-medium">Master Project:</span> {extractionData.tempMasterProjectName}</p>
                                <p><span className="font-medium">Project:</span> {extractionData.tempProjectName}</p>
                            </div>
                        </div>
                    )}

                    {/* Master Project Selection */}
                    <div className="space-y-1">
                        <div className="relative">
                            <FormField
                                fieldKey="masterProject"
                                label="Master Project"
                                type="list"
                                required={true}
                                data={pageLoadData?.masterProjects || []}
                                value={formData.masterProject}
                                error={errs?.masterProject}
                                handleChange={handleInputChange}
                                tooltipText={constants.extraction.tooltipTexts?.masterProject}
                                disabled={false}
                            />
                        </div>
                    </div>

                    {/* Project Selection */}
                    <div className="space-y-1">
                        <div className="relative">
                            <FormField
                                fieldKey="project"
                                label="Project"
                                type="list"
                                required={true}
                                data={pageLoadData?.projects || []}
                                value={formData.project}
                                error={errs?.project}
                                handleChange={handleInputChange}
                                tooltipText={constants.extraction.tooltipTexts?.project}
                                loading={projectsLoading && !!formData.masterProject}
                                disabled={!formData.masterProject}
                            />
                        </div>
                    </div>

                    {/* Approximate Starting Price */}
                    <div className="space-y-1">
                        <FormField
                            fieldKey="approxStartingPrice"
                            label="Approximate Starting Price"
                            type="input"
                            required={true}
                            value={formData.approxStartingPrice}
                            error={errs?.approxStartingPrice}
                            handleChange={handleInputChange}
                            tooltipText={constants.extraction.tooltipTexts?.approxStartingPrice}
                            disabled={metaDataLoading}
                        />
                        {metaDataLoading && <small className="text-gray-400">Loading...</small>}
                    </div>

                    {/* Pincode */}
                    <div className="space-y-1">
                        <FormField
                            fieldKey="pincode"
                            label="Pincode"
                            type="input"
                            required={true}
                            value={formData.pincode}
                            error={errs?.pincode}
                            handleChange={handleInputChange}
                            tooltipText={constants.extraction.tooltipTexts?.pincode}
                            onKeyDown={isAllowedPincodeKey}
                            disabled={metaDataLoading}
                        />
                        {metaDataLoading && <small className="text-gray-400">Loading...</small>}
                    </div>
                </div>
                
                <AlertDialogFooter>
                    <AlertDialogCancel onClick={onClose}>Cancel</AlertDialogCancel>
                    <Button type="button" onClick={saveProjectDetails}>Update</Button>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
};

export default UpdateProjectDetails;